'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import PropTypes from "prop-types";
import { usePixelatedConfig } from '../config/config.client';
import { SmartImage } from '../elements/smartimage';
import "../../css/pixelated.grid.scss";
import "./timeline.css";
// https://www.w3schools.com/howto/howto_css_timeline.asp
/**
 * Timeline — vertical timeline display composed of dated or staged items with optional images and directional layout.
 *
 * @param {arrayOf} [props.timelineData] - Array of items each containing title, content, image, and direction.
 * @param {string} [props.title] - Item title text.
 * @param {string} [props.content] - Item content or description.
 * @param {string} [props.image] - Optional image URL for the timeline item.
 * @param {string} [props.direction] - Visual positioning key (e.g., 'left' or 'right').
 */
Timeline.propTypes = {
    /** Array of timeline item objects with title, content, image and direction. */
    timelineData: PropTypes.arrayOf(PropTypes.shape({
        title: PropTypes.string.isRequired,
        content: PropTypes.string,
        image: PropTypes.string,
        direction: PropTypes.string.isRequired
    })).isRequired
};
export function Timeline(props) {
    return (_jsx("div", { className: "timeline", children: props.timelineData.map((item, index) => item ? (_jsx(TimelineItem, { title: item.title ?? '', content: item.content, image: item.image, direction: item.direction }, index)) : null) }));
}
/**
 * TimelineItem — Single item for the vertical timeline, including optional image and placement direction.
 *
 * @param {string} [props.title] - Title text for the timeline item (required).
 * @param {string} [props.content] - Description or content of the timeline item.
 * @param {string} [props.image] - Optional image URL to display for the item.
 * @param {string} [props.direction] - Visual alignment for the item (e.g., 'left' or 'right').
 */
TimelineItem.propTypes = {
    /** Item title */
    title: PropTypes.string.isRequired,
    /** Item content or description */
    content: PropTypes.string,
    /** Optional image URL for the item */
    image: PropTypes.string,
    /** Placement direction ('left'|'right') */
    direction: PropTypes.string.isRequired
};
export default function TimelineItem(props) {
    const config = usePixelatedConfig();
    return (_jsx("div", { className: "timeline-container timeline-" + props.direction, suppressHydrationWarning: true, children: _jsx("div", { className: "timeline-content", children: _jsxs("div", { className: "row-3col", children: [_jsx("div", { className: "grid-s1-e2", children: _jsx(SmartImage, { src: props.image || "", title: props.title, alt: props.title, className: "timeline-image", cloudinaryEnv: config?.integrations?.cloudinary?.product_env ?? undefined }) }), _jsxs("div", { className: "grid-s2-e4", children: [_jsx("h2", { children: props.title }), _jsx("p", { children: props.content })] })] }) }) }));
}
